import { INSTRUMENT_LABELS, SECTION_LABELS, activePattern } from '../domain/factories';
import * as ops from '../domain/projectOps';
import {
  BPM_MAX,
  BPM_MIN,
  type GenerationOutput,
  type GenerationRequest,
  type Instrument,
  type Pattern,
  type Project,
  type SectionKind,
} from '../domain/schemas';
import { createId, nowIso } from '../lib/ids';
import { applyComposition, describeComposition, drumsFrom, insertSectionAt, resolveSpec } from '../music/generator/apply';
import type { Composition } from '../music/generator/composer';
import { defaultParams } from '../music/generator/composer';
import { randomSeed } from '../music/random';
import { keyLabel } from '../music/theory';
import { toRequestParams } from '../providers/proceduralProvider';
import type { ProviderRegistry } from '../providers/registry';
import { applyAnimationCommand, type AnimationSelection } from './animationCommands';
import { isAnimCommand } from './animationGrammar';
import { applyAudioCommand, type PromptSelection } from './audioCommands';
import { applySceneCommand, type SceneCommandResult, type SceneSelection } from './sceneCommands';
import { isSceneCommand } from './sceneGrammar';
import { isAudioCommand } from './audioGrammar';
import { applyDraft, runMotionSteps, type MotionContext, type MotionDraft } from './motionCommands';
import { isMotionCommand } from './motionGrammar';
import type { Command } from './commands';
import { applySyncCommand, type SyncCommandResult, type SyncPromptSelection } from './syncCommands';
import { isSyncCommand } from './syncGrammar';

export type WorkspaceTab = 'music' | 'animation' | 'sync' | 'visualizer' | 'audio' | 'assistant' | 'settings';

export interface ExecutorContext {
  getProject(): Project;
  update(mutate: (project: Project) => Project): void;
  audio: { start(): Promise<void>; stop(): Promise<void> };
  animation: { start(): void; pause(): void; reset(): void; restart?(): void };
  navigate(tab: WorkspaceTab): void;
  registry: ProviderRegistry;
  /** Renders the arrangement and downloads a WAV; resolves with a summary line. */
  exportWav?: () => Promise<string>;
  randomSeed?: () => number;
  /** Explicit UI selection used by "the selected …" commands. */
  selection?: PromptSelection & SceneSelection & AnimationSelection;
  /** Updates the scene-editor selection after a commit (new object, new character, new animation…). */
  selectScene?: (patch: NonNullable<SceneCommandResult['select']> & { animationId?: string | null }) => void;
  /** Moves the clip selection (e.g. to a newly placed or duplicated clip) after a commit. */
  selectClip?: (clipId: string | null) => void;
  /** Id factory for new clips (deterministic in tests). */
  newId?: () => string;
  /** The Motion Composer (Phase 3C): its draft and path, and where interpreted plans go. */
  motion?: MotionContext & { propose?(draft: MotionDraft): void; applied?(): void };
  /** Audio-visual sync (Phase 4A): the Sync tab's selection, playback of the selected session. */
  sync?: {
    selection: { sessionId: string | null; mappingId: string | null };
    select?(patch: NonNullable<SyncCommandResult['select']>): void;
    /** Plays the selected session (after this prompt's changes are committed). */
    play(sessionId: string | null): Promise<void> | void;
    pause(): void;
  };
}

/**
 * Execution contract — prompts run in three stages:
 *
 *  1. Local generation (e.g. procedural patterns) — no state is touched.
 *  2. Project changes — every change is computed first and committed as ONE update.
 *     Stages 1–2 are all-or-nothing: on any failure nothing is committed and no
 *     transport effect runs (`status: 'failed'`, `projectChanged: false`).
 *  3. Effects (play/stop audio, WAV export, start/pause/reset animation) — run after the
 *     commit, in prompt order. They are irreversible, so they are never rolled back and
 *     a later failure does NOT undo the committed project changes. On the first failing
 *     effect execution stops and the remaining effects are reported as `skipped`.
 *
 * `status` is therefore:
 *  - 'success' — everything applied;
 *  - 'partial' — something took effect (committed project changes and/or earlier
 *    effects) and then an effect failed; `applied` lists exactly what took effect;
 *  - 'failed'  — nothing took effect.
 */
export type ExecutionStatus = 'success' | 'partial' | 'failed';

export interface ExecutionFailure {
  /** The command that failed, or null when the failure is not tied to one command. */
  command: Command['type'] | null;
  message: string;
}

export interface ExecutionResult {
  status: ExecutionStatus;
  /** True when project changes were committed; they stay committed whatever `status` is. */
  projectChanged: boolean;
  /** What took effect, in order (committed changes first, then effects). */
  applied: string[];
  /** Why execution stopped, when status is not 'success'. */
  failure?: ExecutionFailure;
  /** Effects not attempted because an earlier effect failed. */
  skipped: Command['type'][];
}

const errorMessage = (error: unknown, fallback: string) =>
  error instanceof Error && error.message ? error.message : fallback;

const failed = (command: Command['type'] | null, message: string): ExecutionResult => ({
  status: 'failed',
  projectChanged: false,
  applied: [],
  failure: { command, message },
  skipped: [],
});

type Effect = { type: Command['type']; run: () => Promise<string | void> | void; message: string; failVerb: string };

function effectFor(command: Command, ctx: ExecutorContext): Effect | null {
  switch (command.type) {
    case 'music.play':
      return { type: command.type, run: () => ctx.audio.start(), message: 'Playback started.', failVerb: 'start audio' };
    case 'music.stop':
      return { type: command.type, run: () => ctx.audio.stop(), message: 'Playback stopped.', failVerb: 'stop audio' };
    case 'export.wav':
      return {
        type: command.type,
        run: () => {
          if (!ctx.exportWav) throw new Error('WAV export is not available here.');
          return ctx.exportWav();
        },
        message: 'WAV exported.',
        failVerb: 'export the WAV',
      };
    case 'animation.start':
      return { type: command.type, run: () => ctx.animation.start(), message: 'Animation playing.', failVerb: 'start the animation' };
    case 'animation.pause':
      return { type: command.type, run: () => ctx.animation.pause(), message: 'Animation paused.', failVerb: 'pause the animation' };
    case 'animation.reset':
      return { type: command.type, run: () => ctx.animation.reset(), message: 'Animation reset.', failVerb: 'reset the animation' };
    case 'sync.play':
      return {
        type: command.type,
        run: () => {
          if (!ctx.sync) throw new Error('Sync playback is not available here.');
          return ctx.sync.play(null);
        },
        message: 'Playing the synchronized preview.',
        failVerb: 'play the session',
      };
    case 'sync.pause':
      return { type: command.type, run: () => ctx.sync?.pause(), message: 'Sync preview paused.', failVerb: 'pause the session' };
    case 'animation.restart':
      return {
        type: command.type,
        run: () => (ctx.animation.restart ? ctx.animation.restart() : (ctx.animation.reset(), ctx.animation.start())),
        message: 'Animation restarted from frame 0.',
        failVerb: 'restart the animation',
      };
    default:
      return null;
  }
}

const LABEL = (i: Instrument) => INSTRUMENT_LABELS[i].toLowerCase();
const KIND = (k: SectionKind) => SECTION_LABELS[k].toLowerCase();
const signed = (n: number) => `${n > 0 ? '+' : n < 0 ? '−' : '±'}${Math.abs(n)}`;

function firstSection(project: Project, kind: SectionKind) {
  const index = project.arrangement.sections.findIndex((s) => s.kind === kind);
  if (index < 0) throw new ops.OpError(`There is no ${KIND(kind)} section in the arrangement.`);
  return { index, section: project.arrangement.sections[index]! };
}

const patternById = (project: Project, id: string): Pattern => project.patterns.find((p) => p.id === id) ?? activePattern(project);

/** Applies one project-changing command. Returns a description, or null for effect-only commands. */
function applyToProject(project: Project, command: Command, generated: Map<Command, Composition>): { project: Project; message: string } | null {
  switch (command.type) {
    case 'music.play':
      return command.mode && command.mode !== project.transport.mode
        ? { project: ops.setTransportMode(project, command.mode), message: `Switched to ${command.mode} mode.` }
        : null;
    case 'music.setBpm':
      return { project: ops.setBpm(project, command.bpm), message: `Tempo set to ${command.bpm} BPM.` };
    case 'music.nudgeBpm': {
      const next = Math.min(BPM_MAX, Math.max(BPM_MIN, project.settings.bpm + command.delta));
      return { project: ops.setBpm(project, next), message: `Tempo is now ${next} BPM.` };
    }
    case 'music.setSwing':
      return { project: ops.setSwing(project, command.swing), message: `Swing set to ${Math.round(command.swing * 100)}%.` };
    case 'music.mute':
      return { project: ops.setMuted(project, command.instrument, command.muted), message: `${command.muted ? 'Muted' : 'Unmuted'} ${LABEL(command.instrument)}.` };
    case 'music.solo':
      return { project: ops.setSolo(project, command.instrument, command.solo), message: `${command.solo ? 'Soloed' : 'Un-soloed'} ${LABEL(command.instrument)}.` };
    case 'music.pan':
      return { project: ops.setPan(project, command.instrument, command.pan), message: `Panned ${LABEL(command.instrument)} to ${command.pan === 0 ? 'center' : `${Math.abs(command.pan * 100)}% ${command.pan < 0 ? 'left' : 'right'}`}.` };
    case 'music.clear': {
      const pattern = activePattern(project);
      return { project: ops.clearInstrument(project, pattern.id, command.instrument), message: `Cleared ${LABEL(command.instrument)} in "${pattern.name}".` };
    }
    case 'music.resetPattern': {
      const pattern = activePattern(project);
      return { project: ops.resetPatternToDemo(project, pattern.id), message: `Restored the demo groove in "${pattern.name}".` };
    }
    case 'music.randomize': {
      const c = generated.get(command)!;
      const pattern = activePattern(project);
      return {
        project: ops.setPatternNotes(project, pattern.id, drumsFrom(c, pattern.lengthSteps)),
        message: `Re-rolled the drums in "${pattern.name}" with the local procedural composer (seed ${c.params.seed}).`,
      };
    }
    case 'music.generate': {
      const c = generated.get(command)!;
      return { project: applyComposition(project, c), message: describeComposition(c) };
    }
    case 'music.adjustIntensity': {
      const what = command.group === 'all' ? 'everything' : command.group === 'bass' ? 'the 808' : `the ${command.group}`;
      return {
        project: ops.adjustIntensity(project, command.group, command.direction, project.generator?.seed ?? 1),
        message: `Made ${what} ${command.direction > 0 ? 'more' : 'less'} intense (velocity ${command.direction > 0 ? '+15%' : '−15%'}, drive ${command.direction > 0 ? '+0.1' : '−0.1'}${command.group === 'drums' ? ', ghost notes adjusted' : ''}).`,
      };
    }
    case 'music.adjustVolume': {
      if (command.target === 'master') {
        const next = ops.setMasterVolume(project, project.settings.masterVolumeDb + command.deltaDb);
        return { project: next, message: `Master volume ${signed(command.deltaDb)} dB → ${next.settings.masterVolumeDb} dB.` };
      }
      const next = ops.adjustTrackVolume(project, command.target, command.deltaDb);
      const db = next.tracks.find((t) => t.instrument === command.target)!.volumeDb;
      return { project: next, message: `${INSTRUMENT_LABELS[command.target]} volume ${signed(command.deltaDb)} dB → ${db} dB.` };
    }
    case 'music.setVolume':
      return command.target === 'master'
        ? { project: ops.setMasterVolume(project, command.db), message: `Master volume set to ${command.db} dB.` }
        : { project: ops.setTrackVolume(project, command.target, command.db), message: `${INSTRUMENT_LABELS[command.target]} volume set to ${command.db} dB.` };
    case 'music.adjustEffect': {
      const track = project.tracks.find((t) => t.instrument === command.instrument);
      if (!track) throw new ops.OpError(`This project has no ${LABEL(command.instrument)} track.`);
      const next = ops.setEffect(project, command.instrument, command.effect, track.effects[command.effect] + command.delta);
      const value = next.tracks.find((t) => t.instrument === command.instrument)!.effects[command.effect];
      const name = { drive: 'Distortion', reverbSend: 'Reverb send', delaySend: 'Delay send' }[command.effect];
      return { project: next, message: `${name} on ${LABEL(command.instrument)} is now ${Math.round(value * 100)}%.` };
    }
    case 'music.setKey':
      return {
        project: ops.setKey(project, command.key),
        message: `Key set to ${keyLabel(command.key.root, command.key.scale)} (used by new compositions and the note editor; existing notes are not transposed).`,
      };
    case 'music.setMode':
      return { project: ops.setTransportMode(project, command.mode), message: `Switched to ${command.mode} mode.` };
    case 'music.loopSong':
      return { project: ops.setLoopSong(project, command.loop), message: command.loop ? 'The song will loop.' : 'The song will play once.' };
    case 'arrangement.insert': {
      const { index, section } = firstSection(project, command.anchor);
      const at = command.relation === 'before' ? index : index + 1;
      const reference = command.kind === 'buildup' && command.relation === 'after'
        ? patternById(project, project.arrangement.sections[index + 1]?.patternId ?? section.patternId)
        : patternById(project, section.patternId);
      const result = insertSectionAt(project, command.kind, at, reference);
      return { project: result.project, message: `Added a ${KIND(command.kind)} ${command.relation} the ${KIND(command.anchor)}, ${result.detail}.` };
    }
    case 'arrangement.append': {
      const sections = project.arrangement.sections;
      const anchor = command.at === 'start' ? sections[0] : sections.at(-1);
      const reference = anchor ? patternById(project, anchor.patternId) : activePattern(project);
      const result = insertSectionAt(project, command.kind, command.at === 'start' ? 0 : sections.length, reference);
      return { project: result.project, message: `Added a ${KIND(command.kind)} at the ${command.at}, ${result.detail}.` };
    }
    case 'arrangement.remove': {
      const { section } = firstSection(project, command.kind);
      if (project.arrangement.sections.length <= 1) throw new ops.OpError('The arrangement needs at least one section.');
      return { project: ops.removeSection(project, section.id), message: `Removed the ${KIND(command.kind)} (${section.bars} bars).` };
    }
    case 'arrangement.duplicate': {
      const { section } = firstSection(project, command.kind);
      return { project: ops.duplicateSection(project, section.id), message: `Duplicated the ${KIND(command.kind)} (${section.bars} bars).` };
    }
    case 'arrangement.resize': {
      const { section } = firstSection(project, command.kind);
      const bars = section.bars + command.deltaBars;
      return { project: ops.resizeSection(project, section.id, bars), message: `The ${KIND(command.kind)} is now ${bars} bars.` };
    }
    case 'animation.setBackground':
      return { project: ops.setBackground(project, command.color), message: `Background set to ${command.color}.` };
    case 'animation.setSpeed':
      return { project: ops.setSceneSpeed(project, command.speed), message: `Animation speed set to ${command.speed}×.` };
    case 'animation.addShape':
      return { project: ops.addShape(project, command.shape, command.color, command.motion), message: `Added a ${command.shape} (${command.motion}).` };
    case 'animation.clearShapes':
      return { project: ops.clearShapes(project), message: 'Removed every shape from the scene.' };
    default:
      return null;
  }
}

/** Executes parsed commands according to the contract documented on `ExecutionResult`. */
export async function executeCommands(commands: Command[], ctx: ExecutorContext): Promise<ExecutionResult> {
  const generated = new Map<Command, Composition>();
  const history: { request: GenerationRequest; output: GenerationOutput }[] = [];
  const seedFn = ctx.randomSeed ?? randomSeed;

  // Stage 1: run the local procedural composer up front (may fail; nothing is applied yet).
  for (const command of commands) {
    if (command.type !== 'music.generate' && command.type !== 'music.randomize') continue;
    const provider = ctx.registry.forKind('procedural');
    if (!provider) return failed(command.type, 'No procedural provider is registered.');
    const project = ctx.getProject();
    const params =
      command.type === 'music.generate'
        ? resolveSpec(command.spec, seedFn)
        : {
            ...(project.generator ?? defaultParams('brazilian-phonk', { bpm: project.settings.bpm, key: project.settings.key })),
            durationSec: 30,
            seed: command.seed ?? seedFn(),
          };
    const request: GenerationRequest = {
      id: createId('req'),
      kind: 'procedural',
      providerId: provider.capabilities.providerId,
      prompt: command.type === 'music.generate' ? 'compose' : 'randomize drums',
      params: toRequestParams(params),
      status: 'running',
      createdAt: nowIso(),
    };
    try {
      const result = await ctx.registry.generate(request);
      generated.set(command, result.payload as Composition);
      history.push({ request: { ...request, status: 'succeeded' }, output: result.output });
    } catch (error) {
      return failed(command.type, errorMessage(error, 'Generation failed.'));
    }
  }

  // Stage 2: compute all project changes, then commit once.
  const applied: string[] = [];
  let project = ctx.getProject();
  let changed = false;
  // Selection follows commands within one prompt ("place … then reverse the selected clip").
  let selection = ctx.selection ? { ...ctx.selection } : undefined;
  let selectionChanged = false;
  let scenePatch: (NonNullable<SceneCommandResult['select']> & { animationId?: string | null }) | null = null;
  const newId = ctx.newId ?? (() => createId('clp'));
  const motionSteps: Extract<Command, { type: 'motion.step' }>[] = [];
  let motionDraft: MotionDraft | null = null;
  let draftApplied = false;
  let syncSelection: SyncPromptSelection = { ...ctx.sync?.selection, objectIds: ctx.selection?.objectIds, animationId: ctx.selection?.animationId };
  let syncPatch: NonNullable<SyncCommandResult['select']> | null = null;
  for (const command of commands) {
    try {
      if (isSyncCommand(command)) {
        const result = applySyncCommand(project, command as Parameters<typeof applySyncCommand>[1], syncSelection);
        if (!result) continue;
        if (result.project !== project) changed = true;
        project = result.project;
        applied.push(result.message);
        if (result.select) {
          // Later commands of the prompt act on the session just created or used.
          syncSelection = { ...syncSelection, ...result.select };
          syncPatch = { ...(syncPatch ?? {}), ...result.select };
        }
        continue;
      }
      if (command.type === 'motion.step') {
        // The choreography steps of a prompt form one plan (handled after the other commands,
        // so "create a six-second animation with …" plans into the new animation).
        motionSteps.push(command);
        continue;
      }
      if (command.type === 'motion.apply') {
        const result = applyDraft(project, selection, ctx.motion);
        project = result.project;
        changed = true;
        draftApplied = true;
        applied.push(result.message);
        continue;
      }
      if (isAnimCommand(command)) {
        const result = applyAnimationCommand(project, command, selection);
        if (!result) continue;
        project = result.project;
        changed = true;
        applied.push(result.message);
        if (result.select) {
          selection = { sampleId: selection?.sampleId ?? null, clipId: selection?.clipId ?? null, ...selection, ...result.select };
          scenePatch = { ...(scenePatch ?? {}), ...result.select };
        }
        continue;
      }
      if (isSceneCommand(command)) {
        const result = applySceneCommand(project, command, selection);
        if (!result) continue;
        project = result.project;
        changed = true;
        applied.push(result.message);
        if (result.select) {
          // Later commands in the same prompt see the new selection ("create … then place it").
          selection = { sampleId: selection?.sampleId ?? null, clipId: selection?.clipId ?? null, ...selection, ...result.select };
          scenePatch = { ...(scenePatch ?? {}), ...result.select };
        }
        continue;
      }
      if (isAudioCommand(command)) {
        const result = applyAudioCommand(project, command, selection, newId);
        if (!result) continue;
        project = result.project;
        changed = true;
        applied.push(result.message);
        if (result.selectClipId !== undefined) {
          selection = { sampleId: selection?.sampleId ?? null, clipId: result.selectClipId };
          selectionChanged = true;
        }
        continue;
      }
      const result = applyToProject(project, command, generated);
      if (!result) continue;
      project = result.project;
      changed = true;
      applied.push(result.message);
    } catch (error) {
      return failed(command.type, errorMessage(error, 'Command failed.'));
    }
  }
  if (motionSteps.length) {
    try {
      const result = runMotionSteps(project, motionSteps, selection, ctx.motion);
      if (result.project !== project) changed = true;
      project = result.project;
      applied.push(result.message);
      motionDraft = result.draft ?? null;
    } catch (error) {
      return failed('motion.step', errorMessage(error, 'The motion could not be planned.'));
    }
  }
  if (history.length) {
    project = {
      ...project,
      generationRequests: [...project.generationRequests, ...history.map((h) => h.request)].slice(-1000),
      outputs: [...project.outputs, ...history.map((h) => h.output)].slice(-1000),
    };
  }
  if (changed) {
    const final = project;
    try {
      ctx.update(() => final);
    } catch (error) {
      return failed(null, errorMessage(error, 'Could not apply the changes.'));
    }
    if (selectionChanged) ctx.selectClip?.(selection?.clipId ?? null);
    if (scenePatch) ctx.selectScene?.(scenePatch);
  }
  if (syncPatch) ctx.sync?.select?.(syncPatch);
  if (draftApplied) ctx.motion?.applied?.();
  if (motionDraft) ctx.motion?.propose?.(motionDraft);

  // Stage 3: irreversible effects, in prompt order. Never rolled back.
  const visual = (c: Command) => c.type.startsWith('animation.') || isSceneCommand(c) || isAnimCommand(c) || isMotionCommand(c);
  const touchesAnimation = commands.some(visual);
  const touchesAudio = commands.some(isAudioCommand);
  const touchesMusic = commands.some((c) => !visual(c) && !isAudioCommand(c) && !isSyncCommand(c));
  if (commands.some(isSyncCommand)) ctx.navigate('sync');
  else if (touchesAudio && !touchesAnimation) ctx.navigate('audio');
  else if (touchesAnimation && !touchesMusic) ctx.navigate('animation');
  else if (touchesMusic && !touchesAnimation) ctx.navigate('music');

  const effects = commands.map((c) => effectFor(c, ctx)).filter((e): e is Effect => e !== null);
  for (const [index, effect] of effects.entries()) {
    try {
      const detail = await effect.run();
      applied.push(typeof detail === 'string' && detail ? detail : effect.message);
    } catch (error) {
      return {
        status: applied.length > 0 ? 'partial' : 'failed',
        projectChanged: changed,
        applied,
        failure: { command: effect.type, message: `Could not ${effect.failVerb}: ${errorMessage(error, 'unknown error')}` },
        skipped: effects.slice(index + 1).map((e) => e.type),
      };
    }
  }
  return { status: 'success', projectChanged: changed, applied, skipped: [] };
}
